import { differenceInSeconds } from 'date-fns';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { BonusCardTimer } from './BonusCardTimer';
import { BonusTerms } from './Terms';
import { AnalyticsEventName } from '@/api/analytics';
import {
  Bonus,
  Freespin,
  playerBonusesActivation,
  playerFreespinsActivation,
} from '@/api/common';
import { GameV1 } from '@/api/games';
import { Button, ButtonClose } from '@/components/buttons';
import { InfoButton } from '@/components/buttons/InfoButton';
import { ImageComponent } from '@/components/Image';
import {
  ProgressBar,
  ValuePosition,
} from '@/components/progressBars/LinearProgressBar';
import { amountWithCurrencySymbol } from '@/helpers/currency';
import { mapGameV2ToGameV1 } from '@/helpers/game';
import { showTooltip } from '@/helpers/tooltip';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import { useAnalytics } from '@/hooks/analytics';
import { useNavigation } from '@/hooks/navigation';
import { isBonus, PERCENTS_100 } from '@/modules/promotions/helpers';
import { useCommonStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';

import { ScBonusCard } from './styled';
import { ProgressBarMode } from '@/components/progressBars/LinearProgressBar/styled';

const MIN_LONG_TEXT = 18;

const commonButtonProps = (isPending: boolean) => ({
  small: true,
  wide: true,
  noMinWidth: true,
  disabled: isPending,
});

export interface BonusFlipItemProps {
  bonus: Bonus | Freespin;
}

export const BonusFlipItem: FC<BonusFlipItemProps> = ({ bonus }) => {
  const { t } = useTranslation('bonuses');
  const { sendEventToAnalytics } = useAnalytics();

  const {
    currencies,
    gameCategoriesList,
    fetchGamesByIds,
    fetchBonuses,
    fetchFreespins,
  } = useCommonStore(
    useShallow((state) => ({
      currencies: state.currencies,
      gameCategoriesList: state.gameCategoriesList,
      fetchGamesByIds: state.fetchGamesByIds,
      fetchBonuses: state.fetchBonuses,
      fetchFreespins: state.fetchFreespins,
    }))
  );

  const { navigate } = useNavigation();

  const [isFlipped, toggleFlip] = useState(false);
  const [pendingBonusActivation, setPendingBonusActivation] = useState(false);
  const [pendingFreespinsActivation, setPendingFreespinsActivation] =
    useState(false);
  const [bonusGames, setBonusGames] = useState<GameV1[]>();
  const [pendingBonusGames, setPendingBonusGames] = useState(false);

  const {
    validUntil,
    title,
    currency,
    activatable,
    activatableUntil,
    cancelable,
    id,
  } = bonus;

  const isPending = useMemo(
    () =>
      pendingBonusActivation || pendingFreespinsActivation || pendingBonusGames,
    [pendingBonusActivation, pendingFreespinsActivation, pendingBonusGames]
  );

  const isBonusType = useMemo(() => isBonus(bonus), [bonus]);

  const {
    amountCents,
    amountWagerCents,
    amountWagerRequirementCents,
    freespinsTotal,
    gamesInfo,
  } = useMemo(
    () =>
      isBonus(bonus)
        ? {
            amountCents: bonus.amountCents,
            amountWagerCents: bonus.amountWagerCents,
            amountWagerRequirementCents: bonus.amountWagerRequirementCents,
          }
        : {
            freespinsTotal: bonus.freespinsTotal,
            gamesInfo: bonus.gamesInfo,
          },
    [bonus]
  );

  const timeLeft = useMemo(() => {
    const untilDate = activatable ? activatableUntil : validUntil;

    if (!untilDate) {
      return 0;
    }

    return differenceInSeconds(new Date(untilDate), new Date());
  }, [activatable, activatableUntil, validUntil]);

  const bonusAmount = useMemo(() => {
    if (!amountCents) return;
    const bonusAmountData = getCurrencyCentsAndCode(
      amountCents,
      currency,
      currencies
    );

    return amountWithCurrencySymbol(
      bonusAmountData.currency,
      bonusAmountData.amount
    ).toString();
  }, [amountCents, currencies, currency]);

  const handleActivate = useCallback(async () => {
    const error = await (async () => {
      let responseError;

      try {
        if (isBonusType) {
          setPendingBonusActivation(true);
          const { error } = await playerBonusesActivation(id);

          if (!error) await fetchBonuses();

          responseError = error;
        } else {
          setPendingFreespinsActivation(true);
          const { error } = await playerFreespinsActivation(id);

          if (!error) await fetchFreespins();

          responseError = error;
        }
      } catch (e) {
        console.log(e);
      } finally {
        if (isBonusType) {
          setPendingBonusActivation(false);
        } else {
          setPendingFreespinsActivation(false);
        }
      }

      return responseError;
    })();

    const bonusType = isBonusType ? 'bonus' : 'freespin';

    if (error) {
      sendEventToAnalytics({
        name: AnalyticsEventName.PROMO__BONUS_CARD__ACTIVATE__FAILURE,
        attributes: {
          additionalData: JSON.stringify({
            type: bonusType,
            id,
          }),
        },
      });

      showTooltip({
        type: TooltipTypes.ERROR,
        body: t('sthWentWrong'),
      });
    } else {
      showTooltip({
        type: TooltipTypes.SUCCESS,
        body: (
          <div>
            <p className="title">{t('tooltip.bonusActivationSuccess.title')}</p>
            <p>{t('tooltip.bonusActivationSuccess.description')}</p>
          </div>
        ),
      });

      sendEventToAnalytics({
        name: AnalyticsEventName.PROMO__BONUS_CARD__ACTIVATE__SUCCESS,
        attributes: {
          additionalData: JSON.stringify({
            type: bonusType,
            id,
          }),
        },
      });
    }
  }, [fetchBonuses, fetchFreespins, id, isBonusType, sendEventToAnalytics, t]);

  const handlePlay = useCallback(() => {
    if (!gameCategoriesList) return;

    sendEventToAnalytics({
      name: AnalyticsEventName.PROMO__BONUS_CARD__PLAY,
    });

    const link = bonusGames?.length
      ? `/game/${bonusGames[0].fullPath}`
      : `/games/wagering#gamesSection`;

    navigate(link);
  }, [bonusGames, gameCategoriesList, navigate, sendEventToAnalytics]);

  const wagerInfo = useMemo(() => {
    if (
      amountCents === undefined ||
      amountWagerCents === undefined ||
      amountWagerRequirementCents === undefined
    )
      return;

    const wagerPercent =
      amountWagerRequirementCents !== 0
        ? (amountWagerCents * PERCENTS_100) / amountWagerRequirementCents
        : amountWagerRequirementCents;

    const wagerAmount =
      amountWagerRequirementCents !== 0
        ? `${amountWagerRequirementCents / amountCents}x WG`
        : `${amountWagerRequirementCents}x WG`;

    const { amount: amountWager } = getCurrencyCentsAndCode(
      amountWagerCents,
      currency,
      currencies
    );

    const { amount: amountWagerRequirement, currency: code } =
      getCurrencyCentsAndCode(
        amountWagerRequirementCents,
        currency,
        currencies
      );

    const wagerProportion = (
      <>
        {amountWager} / {amountWagerRequirement} {code} {t('wagered')}
      </>
    );

    return { wagerPercent, wagerProportion, wagerAmount };
  }, [
    amountCents,
    amountWagerCents,
    amountWagerRequirementCents,
    currencies,
    currency,
    t,
  ]);

  const isLongText = useMemo(() => {
    const titleLength = bonusAmount
      ? bonusAmount.length
      : t('freespinsX', { freespinsTotal }).length;

    return titleLength > MIN_LONG_TEXT;
  }, [bonusAmount, freespinsTotal, t]);

  useEffect(() => {
    if (!gamesInfo) return;

    (async () => {
      try {
        setPendingBonusGames(true);

        const gameIds = gamesInfo.map(({ identifier }) => identifier);

        const games = await fetchGamesByIds({ gameIds });

        if (!games) return;

        const gamesV1 = mapGameV2ToGameV1(Object.values(games));

        setBonusGames(gamesV1);
      } catch (e) {
        console.log(e);
      } finally {
        setPendingBonusGames(false);
      }
    })();
  }, [fetchGamesByIds, gamesInfo]);

  const buttons = (
    <div className="buttons-container">
      {activatable ? (
        <Button
          primary
          onClick={handleActivate}
          {...commonButtonProps(isPending)}
        >
          {t('activate')}
        </Button>
      ) : (
        <Button primary onClick={handlePlay} {...commonButtonProps(isPending)}>
          {t('play')}
        </Button>
      )}
      {cancelable && (
        <Button
          whiteTransparent
          onClick={() => {
            useCommonStore.setState({
              currentCancelBonusData: { isBonusType, id },
            });
          }}
          {...commonButtonProps(isPending)}
        >
          {t('cancel')}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <ScBonusCard
        isFlipped={isFlipped}
        withWager={!!wagerInfo}
        isLongText={isLongText}
      >
        <div className="front">
          <div className="header">
            <BonusCardTimer timeLeft={timeLeft} />
            <InfoButton handleClick={() => toggleFlip(true)} />
          </div>

          <div className="info">
            <div className="offer-data">
              <div className="title">{title}</div>
              <div className="offer">
                {bonusAmount}
                {freespinsTotal && t('freespinsX', { freespinsTotal })}
              </div>
            </div>

            {wagerInfo && (
              <div className="progress">
                <ProgressBar
                  value={wagerInfo.wagerPercent}
                  showValue={ValuePosition.RIGHT}
                  mode={ProgressBarMode.SECONDARY}
                  isFloorRoundedValue
                />

                {isBonusType && (
                  <div className="wager">
                    <div className="progress">{wagerInfo.wagerProportion}</div>
                    <div className="coef">{wagerInfo.wagerAmount}</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {buttons}

          <ImageComponent
            className="default-bonus-img"
            src="/bonuses/bonus-card-gift.png"
            alt="defualt-bonus-img"
            width={168}
            height={140}
          />
        </div>

        <div className="back">
          <ButtonClose top={0} right={0} onClick={() => toggleFlip(false)} />

          <BonusTerms bonus={bonus} bonusGames={bonusGames} />

          {buttons}
        </div>
      </ScBonusCard>
    </>
  );
};
